// ImageConverter.tsx
import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Zoom,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
import { FiUpload, FiDownload } from "react-icons/fi";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useUserPermissions } from "../components/useUserPermissions.tsx";
import PermissionGuard from "../components/PermissionGuard.tsx";
import { useThemeContext } from "../contexts/ThemeContext.tsx";

// --- Stílusok és Animációk ---
const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
`;

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(4px)',
    backgroundColor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.4)',
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: darkMode ? 'white' : '#1a237e',
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
  },
  '& .MuiDialogTitle-root': { 
    fontWeight: 'bold', 
    textAlign: 'center', 
    fontSize: '1.4rem',
    color: darkMode ? 'white' : '#1a237e',
    paddingTop: '24px'
  },
  '& .MuiDialogContent-root': {
    color: darkMode ? 'rgba(255,255,255,0.85)' : '#1a237e',
  },
  '& .MuiInputLabel-root': {
    color: darkMode ? 'rgba(255,255,255,0.7)' : 'inherit',
  },
  '& .MuiOutlinedInput-root': {
    color: darkMode ? 'white' : 'inherit',
    '& fieldset': {
      borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
    },
    '&:hover fieldset': {
      borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
    },
  },
  '& .MuiSelect-icon': {
    color: darkMode ? 'white' : 'inherit',
  }
}));

const DialogActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '8px 24px',
  fontWeight: 'bold',
  color: darkMode ? '#ffffff' : '#1a237e',
  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
  border: darkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
    transform: 'scale(1.05)',
  },
}));

const DropzoneContainer = styled(Paper)(({ theme }) => ({
  padding: "32px",
  border: "2px dashed #ccc",
  borderRadius: "16px",
  textAlign: "center",
  cursor: "pointer",
  transition: "border 0.3s ease-in-out",
  "&:hover": {
    border: "2px dashed #2196f3",
  },
}));

const PreviewImage = styled("img")(({ theme }) => ({
  display: "block", 
  width: "100%",  
  maxWidth: "100%",
  maxHeight: "300px",
  objectFit: "contain",
  margin: "20px 0",
}));

const CropShapeButton = styled(ToggleButton)(({ theme }) => ({
  padding: "10px 20px",
  margin: "0 5px",
  textTransform: "none", 
}));

const defaultCrop: Crop = { unit: "%", width: 30, height: 30, x: 35, y: 35 };

const ImageConverter = () => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [convertedImage, setConvertedImage] = useState<string | null>(null);
  const [format, setFormat] = useState("jpeg");
  const [error, setError] = useState("");
  const [converting, setConverting] = useState(false);
  const [cropShape, setCropShape] = useState("square");
  const [crop, setCrop] = useState<Crop>(defaultCrop);
  const [completedCrop, setCompletedCrop] = useState<any>(defaultCrop);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const { darkMode } = useThemeContext();
  const { hasPermission } = useUserPermissions();
  
  const requiredRankForCircle = "OnyxOverlord";
  const circlePermission = hasPermission(requiredRankForCircle);

  useEffect(() => {
    if (!circlePermission && cropShape === "circle") {
      setCropShape("square");
    }
  }, [circlePermission, cropShape]);

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
    if (fileRejections.length > 0) {
      setError("Invalid file format or the file is larger than 10MB. SVG files are not supported.");
      setSnackbarMessage("Upload failed! Please check file type and size.");
      setSnackbarOpen(true);
      return;
    }

    const currentFile = acceptedFiles[0];
    if (currentFile) {
      if (currentFile.size > 10 * 1024 * 1024) {
        setError("The maximum file size is 10MB");
        setSnackbarMessage("Upload failed: file too large");
        setSnackbarOpen(true);
        return;
      }

      // Tiszta, pixeles képek (JPG, PNG, WEBP) beolvasása
      setFile(currentFile);
      setPreview(URL.createObjectURL(currentFile));
      setConvertedImage(null);
      setError("");
      setSnackbarMessage("File uploaded!");
      setSnackbarOpen(true);
      setOpenDialog(true);
      setCrop(defaultCrop);
      setCompletedCrop(defaultCrop);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"]
      // SVG szándékosan kihagyva!
    },
    maxSize: 10 * 1024 * 1024,
  });

  const handleResetCrop = () => {
    setCrop(defaultCrop);
    setCompletedCrop(defaultCrop);
  };

  const handleConvert = async () => {
    try {
      setConverting(true);
      const cropData = completedCrop || crop;
      const formData = new FormData();
      formData.append("targetFormat", format);
      formData.append("cropShape", cropShape);
      formData.append("aspectRatio", "free");

      formData.append("cropX", cropData.x.toString());
      formData.append("cropY", cropData.y.toString());
      formData.append("cropWidth", cropData.width.toString());
      formData.append("cropHeight", cropData.height.toString());
      formData.append("cropUnit", cropData.unit);

      if (file) {
        formData.append("file", file);
        if (file.type === "image/png") {
          formData.append("preserveAlpha", "true");
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/user/public/convertpicture`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("An error occurred during conversion");
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      setConvertedImage(imageUrl);
      setSnackbarMessage("Image successfully converted!");
      setOpenDialog(false);
    } catch (error) {
      setSnackbarMessage("Image conversion failed!");
    } finally {
      setConverting(false);
      setSnackbarOpen(true);
    }
  };

  const handleDownload = () => {
    if (convertedImage) {
      const a = document.createElement("a");
      a.href = convertedImage;
      a.download = "converted-image." + format;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleCropShapeChange = (event: any, newShape: string) => {
    if (newShape !== null) {
      if (newShape === "circle" && !circlePermission) return;
      setCropShape(newShape);
    }
  };

  const onCropComplete = (crop: any, percentCrop: any) => {
    setCompletedCrop(percentCrop);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, mt: '64px' }}>
      <style>{keyframes}</style>
      <Typography variant="h4" gutterBottom align="center">
        Picture Converter
      </Typography>

      <Box sx={{ mb: 4 }}>
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <FiUpload size={40} color="#757575" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {isDragActive
              ? "Drag the picture here"
              : "Drag & drop or click to select the image"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Supported formats: PNG, JPEG, JPG, WEBP (Max 10MB) 
          </Typography>
        </DropzoneContainer>

        {convertedImage ? (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <PreviewImage src={convertedImage} alt="Converted image" />
            <Button
              variant="contained"
              color="success"
              startIcon={<FiDownload />}
              onClick={handleDownload}
              sx={{ mt: 2 }}
            >
              Download
            </Button>
          </Box>
        ) : (
          preview && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <PreviewImage src={preview} alt="Preview" />
            </Box>
          )
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}
      </Box>

      <AnimatedDialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md"
        darkMode={darkMode}
        TransitionComponent={Zoom}
        transitionDuration={400}
        disableScrollLock
      >
        <DialogTitle>Image settings</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2, width: '100%' }}>
            
            <FormControl fullWidth>
              <InputLabel>Target Format</InputLabel>
              <Select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                label="Target Format"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: darkMode 
                          ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
                          : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
                      color: darkMode ? 'white' : '#1a237e',
                      border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
                      borderRadius: '12px',
                    }
                  }
                }}
              >
                <MenuItem value="jpeg">JPEG</MenuItem>
                <MenuItem value="jpg">JPG</MenuItem>
                <MenuItem value="png">PNG</MenuItem>
                <MenuItem value="webp">WEBP</MenuItem>
              </Select>
              
              {/* --- JAVÍTOTT NOTE SZÖVEG --- */}
              <Box sx={{ width: '100%', mt: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    width: '100%',
                    whiteSpace: 'normal', // Biztosítja, hogy a szöveg a DialogContent szélességéhez igazodva tördelődjön
                    wordBreak: 'break-word',
                    color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                    fontStyle: 'italic'
                  }}
                >
                  💡 <b>Note:</b> When combining <b>Circle crop</b> with <b>PNG format</b>, the background outside the circle will be completely transparent.
                </Typography>
              </Box>
            </FormControl>

            <Box sx={{ width: '100%' }}>
              <Typography gutterBottom sx={{ fontWeight: 'bold' }}>Crop Shape</Typography>
              <ToggleButtonGroup
                value={cropShape}
                exclusive
                onChange={handleCropShapeChange}
                aria-label="crop shape"
                sx={{ mb: 2 }}
              >
                <CropShapeButton value="square" sx={{ color: darkMode ? 'white' : 'inherit' }}>Square</CropShapeButton>
                <CropShapeButton 
                  value="circle" 
                  disabled={!circlePermission}
                  sx={{ gap: 1, color: darkMode ? 'white' : 'inherit' }}
                >
                  Circle
                  {!circlePermission && <PermissionGuard requiredRank={requiredRankForCircle} />}
                </CropShapeButton>
              </ToggleButtonGroup>

              {/* --- JAVÍTOTT KONTÉNER A KÉPHEZ --- */}
              {preview && (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <Paper 
                    sx={{ 
                      p: 1, // Kisebb padding, hogy jobban rásimuljon a képre
                      mb: 2, 
                      bgcolor: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.7)',
                      width: 'fit-content', // Ez a varázsszó: a konténer csak addig nyúlik, ameddig a kép tart
                      maxWidth: '100%',     // Megakadályozza, hogy kilógjon a Dialog-ból
                      borderRadius: 2,
                      overflow: 'auto'      // Biztosítja a reszponzivitást
                    }}
                  >
                    <ReactCrop
                      crop={crop}
                      onChange={(newCrop, percentCrop) => {
                        setCrop(newCrop);
                        setCompletedCrop(percentCrop);
                      }}
                      circularCrop={cropShape === "circle"}
                      onComplete={onCropComplete}
                    >
                      <img
                        src={preview}
                        style={{
                          display: "block",
                          maxWidth: "100%", // Csak a rendelkezésre álló helyig nőhet
                          maxHeight: "400px",
                          width: "auto",    // A 100% helyett auto, így a natív méretét veszi fel, amihez a fit-content igazodni tud
                          height: "auto",
                          objectFit: "contain",
                        }}
                        alt="Crop preview"
                      />
                    </ReactCrop>
                  </Paper>
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <DialogActionButton darkMode={darkMode} onClick={handleResetCrop}>Reset Crop</DialogActionButton>
          <DialogActionButton
            darkMode={darkMode}
            onClick={handleConvert}
            disabled={converting}
            sx={{ 
              bgcolor: darkMode ? '#ff4081' : '#1976d2', 
              color: 'white',
              '&:hover': { bgcolor: darkMode ? '#f50057' : '#115293' }
            }}
          >
            {converting ? <CircularProgress size={24} color="inherit" /> : "Convert"}
          </DialogActionButton>
          <DialogActionButton darkMode={darkMode} onClick={() => setOpenDialog(false)}>Close</DialogActionButton>
        </DialogActions>
      </AnimatedDialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default ImageConverter;